import { isDefined } from './isDefined'

interface SuccessResult<T> {
  _tag: 'success'
  data: T
}

interface ErrorResult<T> {
  _tag: 'failure'
  error: T
}

export type PipeResult<A, E> = SuccessResult<A> | ErrorResult<E>

type NullableError = 'NullableError'

interface Pipe<A, E = never> {
  map: <R>(fn: (value: A) => R) => Pipe<R, E>
  get: () => A
  getOrElse: <R>(fallback: (e: E) => R) => A | R
  throwNullable: () => Pipe<NonNullable<A>, NullableError>
  throwWhen: <Err>(predicate: (value: A) => boolean, mapError: (value: A) => Err) => Pipe<A, E | Err>
  flatMap: <R>(fn: (value: A) => Pipe<R, E>) => Pipe<R, E>
  mapNullable: <R, Fallback>(fn: (value: A) => R, fallbackFn: () => Fallback) => Pipe<R | Fallback, E>
  mapWhen: <R>(predicate: (value: A) => boolean, fn: (value: A) => R) => Pipe<R | A, E>
  mapError: <RE>(fn: (error: E) => RE) => Pipe<A, RE>
  tap: (fn: (value: A) => unknown) => Pipe<A, E>
  tapWhen: (predicate: (value: A) => boolean, fn: (value: A) => unknown) => Pipe<A, E>
  tapError: (fn: (error: E) => unknown) => Pipe<A, E>
  trySafe: <R, Fail>(fn: (value: A) => R, onError: (e: unknown) => Fail) => Pipe<R | Fail, E>
  try: <R, Fail>(fn: (value: A) => R, onError?: (e: unknown) => Fail) => Pipe<R, E | Fail>
}

const isSuccessResult = <T, E>(result: PipeResult<T, E>): result is SuccessResult<T> => {
  return result._tag === 'success'
}

const isErrorResult = <T, E>(result: PipeResult<T, E>): result is ErrorResult<E> => {
  return result._tag === 'failure'
}

const successValue = <T>(value: T): SuccessResult<T> => {
  return {
    _tag: 'success',
    data: value
  }
}

const errorValue = <E>(error: E): ErrorResult<E> => {
  return {
    _tag: 'failure',
    error
  }
}

const resolveSuccessValue = <A, E>(result: PipeResult<A, E>) => {
  if (isSuccessResult(result)) {
    return result.data
  }

  throw new Error(result.error as unknown as string)
}

export const pipe = <A, E = never>(initFn: () => A): Pipe<A, E> => {
  return pipeInternal(() => successValue(initFn()))
}

export const pipeInternal = <A, E>(initFn: () => PipeResult<A, E>): Pipe<A, E> => {
  const processSuccessValue = <R>(fn: (value: A) => R): Pipe<R, E> => {
    return pipeInternal(() => {
      const result = initFn()
      if (isSuccessResult(result)) {
        return successValue(fn(result.data))
      }

      return result
    })
  }

  const processErrorValue = <R>(fn: (error: E) => R): Pipe<A, R> => {
    return pipeInternal(() => {
      const result = initFn()
      if (isErrorResult(result)) {
        return errorValue(fn(result.error))
      }

      return result
    })
  }

  return {
    map: <R>(fn: (value: A) => R): Pipe<R, E> => {
      return processSuccessValue(fn)
    },
    throwNullable: () => {
      return pipeInternal(() => {
        const result = initFn()
        if (isSuccessResult(result) && isDefined(result.data)) {
          return result as SuccessResult<NonNullable<A>>
        }

        return errorValue('NullableError')
      })
    },
    throwWhen: <Err>(predicate: (value: A) => boolean, mapError: (value: A) => Err) => {
      return pipeInternal<A, E | Err>(() => {
        const result = initFn()
        if (isSuccessResult(result)) {
          return !predicate(result.data) ? result : errorValue(mapError(result.data))
        }

        return result
      })
    },
    get: () => resolveSuccessValue(initFn()),
    getOrElse: <R>(fallback: (error: E) => R) => {
      const result = initFn()
      if (isSuccessResult(result)) {
        return result.data
      }

      return fallback(result.error)
    },
    flatMap: <R>(fn: (value: A) => Pipe<R, E>): Pipe<R, E> => {
      return processSuccessValue((value) => fn(value).get())
    },
    mapNullable: <R, Fallback>(fn: (value: A) => R, fallbackFn: () => Fallback) => {
      return processSuccessValue((value) => (isDefined(value) ? fn(value) : fallbackFn()))
    },
    mapWhen: <R>(predicate: (value: A) => boolean, fn: (value: A) => R) => {
      return processSuccessValue((value) => (predicate(value) ? fn(value) : value))
    },
    mapError: <R>(fn: (error: E) => R) => {
      return processErrorValue(fn)
    },
    tap: (fn: (value: A) => unknown) => {
      return processSuccessValue((value) => {
        fn(value)
        return value
      })
    },
    tapError: (fn: (value: E) => unknown) => {
      return processErrorValue((value) => {
        fn(value)
        return value
      })
    },
    tapWhen: (predicate: (value: A) => boolean, fn: (value: A) => unknown) => {
      return processSuccessValue((value) => {
        if (predicate(value)) {
          fn(value)
        }
        return value
      })
    },
    trySafe: <R, Fail>(fn: (value: A) => R, onError: (e: unknown) => Fail) => {
      return processSuccessValue((value) => {
        try {
          return fn(value)
        } catch (e) {
          return onError(e)
        }
      })
    },
    try: <R, Fail>(fn: (value: A) => R, onError: (e: unknown) => Fail = (e) => e as Fail): Pipe<R, E | Fail> => {
      return pipeInternal<R, E | Fail>(() => {
        const value = initFn()
        if (isSuccessResult(value)) {
          try {
            return successValue(fn(value.data))
          } catch (e) {
            return errorValue(onError(e))
          }
        }
        return value
      })
    }
  }
}
